import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchSelect } from "@/components/ui/search-select";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/utils";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { GST_RATES, proposalTotals } from "@/lib/proposalTotals";

/** An estimate line while editing (numbers kept as typed) */
export interface EditableEstimateLine {
  key: string;
  description: string;
  quantity: string;
  unit_price: string;
  product_id: string | null;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
let seq = 0;
const newKey = () => `l-${Date.now().toString(36)}-${(seq++).toString(36)}`;

export const toEditableLines = (lines: Array<{ description: string; quantity: number | string; unit_price: number | string; product_id?: string | null }> = []) =>
  lines.map((l) => ({ key: newKey(), description: l.description, quantity: String(Number(l.quantity)), unit_price: String(Number(l.unit_price)), product_id: l.product_id || null }));

export const estimateTotal = (lines: EditableEstimateLine[]) =>
  round2(lines.reduce((sum, l) => sum + round2(Number(l.quantity) || 0) * round2(Number(l.unit_price) || 0), 0));

export function estimateProblem(lines: EditableEstimateLine[]): string | null {
  for (const [i, l] of lines.entries()) {
    if (!l.description.trim()) return `Estimate line ${i + 1} needs a description`;
    if (l.quantity === "" || !(Number(l.quantity) >= 0)) return `Estimate line ${i + 1}: enter a quantity of 0 or more`;
    if (l.unit_price === "" || !(Number(l.unit_price) >= 0)) return `Estimate line ${i + 1}: enter a price of 0 or more`;
  }
  return null;
}

export const toApiLines = (lines: EditableEstimateLine[]) =>
  lines.map((l) => ({ description: l.description.trim(), quantity: Number(l.quantity), unit_price: Number(l.unit_price), product_id: l.product_id || null }));

/**
 * The indicative price table of a proposal. Prices are excluding GST; the formal GST quotation
 * comes later, at the Quotation stage.
 */
export function EstimateEditor({
  lines,
  onChange,
  disabled,
  gstRate = null,
  includesGst = false,
  onGstChange,
}: {
  lines: EditableEstimateLine[];
  onChange: (next: EditableEstimateLine[]) => void;
  disabled?: boolean;
  /** GST in percent, or null to show the plain indicative total (older proposals) */
  gstRate?: number | null;
  /** The typed amounts already include GST */
  includesGst?: boolean;
  onGstChange?: (rate: number | null, includesGst: boolean) => void;
}) {
  const canPickProducts = useAuth().hasPermission("product.read");
  const update = (key: string, patch: Partial<EditableEstimateLine>) => onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const add = () => onChange([...lines, { key: newKey(), description: "", quantity: "1", unit_price: "", product_id: null }]);
  const total = estimateTotal(lines);
  const totals = proposalTotals(total, gstRate, includesGst);
  const hasGst = gstRate !== null && gstRate !== undefined;

  return (
    <div className="rounded-xl border overflow-hidden">
      {onGstChange && (
        <div className="flex flex-wrap items-end gap-x-6 gap-y-2 border-b bg-muted/20 px-3 py-2.5">
          <div className="w-44 space-y-1">
            <label htmlFor="est-gst" className="text-[11px] font-semibold text-muted-foreground">GST under the table</label>
            <SearchableSelect id="est-gst" value={hasGst ? String(gstRate) : "none"} disabled={disabled} onChange={(e) => onGstChange(e.target.value === "none" ? null : Number(e.target.value), includesGst)} className="h-8 text-xs">
              <option value="none">Not shown (indicative)</option>
              {GST_RATES.map((r) => <option key={r} value={String(r)}>{r}%</option>)}
            </SearchableSelect>
          </div>
          {hasGst && (
            <label className="flex items-center gap-2 pb-1.5 text-xs">
              <input type="checkbox" className="size-4 accent-primary" checked={includesGst} disabled={disabled} onChange={(e) => onGstChange(gstRate, e.target.checked)} />
              The amounts I type already include GST
            </label>
          )}
          <p className="pb-1.5 text-[11px] text-muted-foreground">
            {hasGst ? (includesGst ? "Type the final prices; the subtotal and GST are worked back from them." : "Type prices before GST; GST is added under the table.") : "Prices are shown as an indicative total, without GST."}
          </p>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="text-left font-semibold px-3 py-2 min-w-[240px]">Description</th>
              <th className="text-right font-semibold px-2 py-2 w-20">Qty</th>
              <th className="text-right font-semibold px-2 py-2 w-32">Unit price (₹{hasGst && includesGst ? ", incl. GST" : ""})</th>
              <th className="text-right font-semibold px-3 py-2 w-32">Amount</th>
              {!disabled && <th className="w-9" />}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.key} className="border-t align-top">
                <td className="px-3 py-2 space-y-1.5">
                  {!disabled && canPickProducts && (
                    <SearchSelect
                      value={l.product_id || ""}
                      selectedLabel={l.product_id ? l.description : ""}
                      endpoint="/api/Product/List"
                      extraParams={{ active: "true" }}
                      placeholder="Pick a product (optional)"
                      allowEmpty
                      emptyLabel="Custom line"
                      getLabel={(p) => `${p.name}${p.code ? ` · ${p.code}` : ""}`}
                      onChange={(id, row) =>
                        update(l.key, row ? { product_id: id, description: row.name, unit_price: String(Number(row.unit_price) || 0) } : { product_id: null })
                      }
                    />
                  )}
                  <input
                    value={l.description}
                    disabled={disabled}
                    maxLength={500}
                    onChange={(e) => update(l.key, { description: e.target.value })}
                    placeholder="What the customer gets"
                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs disabled:border-transparent disabled:bg-transparent disabled:px-0 disabled:opacity-100"
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={l.quantity}
                    disabled={disabled}
                    onChange={(e) => update(l.key, { quantity: e.target.value })}
                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-right disabled:border-transparent disabled:bg-transparent disabled:opacity-100"
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={l.unit_price}
                    disabled={disabled}
                    onChange={(e) => update(l.key, { unit_price: e.target.value })}
                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-right disabled:border-transparent disabled:bg-transparent disabled:opacity-100"
                  />
                </td>
                <td className="px-3 py-2 text-right font-semibold whitespace-nowrap leading-8">
                  {formatINR(round2((Number(l.quantity) || 0) * (Number(l.unit_price) || 0)))}
                </td>
                {!disabled && (
                  <td className="py-2 pr-2">
                    <button
                      type="button"
                      title="Remove line"
                      onClick={() => onChange(lines.filter((x) => x.key !== l.key))}
                      className="size-8 inline-flex items-center justify-center rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {lines.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">
                  No price lines. The proposal will say pricing follows in the quotation.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t bg-muted/30">
              <td className="px-3 py-2">
                {!disabled && (
                  <Button type="button" size="sm" variant="outline" onClick={add} className="h-7 gap-1 text-xs">
                    <Plus className="size-3.5" /> Add line
                  </Button>
                )}
              </td>
              <td colSpan={2} className="px-2 py-2 text-right font-semibold">
                {hasGst ? "Subtotal" : "Indicative total (excl. GST)"}
              </td>
              <td className="px-3 py-2 text-right font-bold whitespace-nowrap">{formatINR(hasGst ? totals.subtotal : total)}</td>
              {!disabled && <td />}
            </tr>
            {hasGst && (
              <>
                <tr className="bg-muted/30">
                  <td />
                  <td colSpan={2} className="px-2 py-1.5 text-right font-semibold">GST @ {gstRate}%</td>
                  <td className="px-3 py-1.5 text-right font-bold whitespace-nowrap">{formatINR(totals.gst)}</td>
                  {!disabled && <td />}
                </tr>
                <tr className="bg-success-soft">
                  <td />
                  <td colSpan={2} className="px-2 py-2 text-right font-semibold">Total (inclusive of GST)</td>
                  <td className="px-3 py-2 text-right font-bold whitespace-nowrap">{formatINR(totals.total)}</td>
                  {!disabled && <td />}
                </tr>
              </>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  );
}
